/**
 * PageHeader: the title row at the top of every dashboard page. Large title (24px semibold, tight tracking), a
 * one-line grey description under it, and the primary action on the right. It sits straight on the page (no card),
 * like the rest of the shell.
 *
 *   <PageHeader title="Logs" />
 *   <PageHeader title="API keys" description="Create and manage the keys your code uses." actions={<Button>Create key</Button>} />
 *   <PageHeader title="Batch" showBack onBack={() => router.back()}>
 *     <SearchBar ... />                                     children are the actions slot (older call sites)
 *   </PageHeader>
 *   <PageHeader title="Scrape" spacing="none" />          no bottom margin (WorkspaceLayout spaces it itself)
 *
 * `variant` is kept for older call sites; "card" and "plain" now render the same plain header.
 * The actions slot sits on the right and takes at least half the row on wider screens; when the title and actions
 * don't fit on one line it wraps under the title at full width (left-aligned on phones) instead of overflowing.
 */
import { ReactNode } from "react";
import IconButton from "@/components/ui/IconButton";

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  backLabel?: string;
  /** Actions slot. `children` works too, for older call sites. */
  actions?: ReactNode;
  children?: ReactNode;
  spacing?: "default" | "none";
  variant?: "card" | "plain";
  className?: string;
}

export default function PageHeader({
  title,
  description,
  showBack = false,
  onBack,
  backLabel = "Back",
  actions,
  children,
  spacing = "default",
  className = "",
}: PageHeaderProps) {
  const slot = actions ?? children;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-3 ${spacing === "default" ? "mb-6" : ""} ${className}`}>
      <div className="flex min-w-0 items-start gap-2">
        {showBack && <IconButton icon="lucide:arrow-left" label={backLabel} size="sm" onClick={onBack} className="-ml-1.5 mt-0.5" />}
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-8 tracking-tight text-fg">{title}</h1>
          {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
        </div>
      </div>
      {slot && (
        <div className="flex w-full flex-wrap items-center justify-start gap-2 text-xs sm:w-auto sm:flex-[1_1_40%] sm:justify-end">
          {slot}
        </div>
      )}
    </div>
  );
}
